import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import ConfirmModal from "./ConfirmModal";
import { useNotifications } from "../hooks/notificationsContext";
import { formatRarityName } from "../utils/formatRarityName";

export default function AssetProfileView({ selectedAsset, onPurchaseSuccess }) {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState("buy"); 
  const [, setStatus] = useState({ type: "", message: "" });
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const { addNotification } = useNotifications();

  const isOwner = auth.currentUser?.uid === selectedAsset?.sellerId;

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!selectedAsset?.itemId) return;
      setLoading(true);
      setStatus({ type: "", message: "" });
      setPurchaseQuantity(1);
      try {
        const itemRef = doc(db, "Item_Database", selectedAsset.itemId);
        const itemSnap = await getDoc(itemRef);
        if (itemSnap.exists()) {
          setMetadata(itemSnap.data());
        }
      } catch (err) {
        console.error("Error fetching item metadata:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [selectedAsset]);

  const handleActionInitiation = (type) => {
    if (!selectedAsset || processing) return;
    if (type === "buy" && (purchaseQuantity < 1 || purchaseQuantity > selectedAsset.quantity)) {
      addNotification("Invalid quantity selected", "error");
      return;
    }
    setConfirmType(type);
    setShowConfirm(true);
  };

  const handleConfirmAction = () => {
    if (confirmType === "buy") handleConfirmPurchase();
    else handleCancelListing();
  };

  const handleCancelListing = async () => {
    setShowConfirm(false);
    setProcessing(true);
    setStatus({ type: "info", message: "CANCELLING..." });

    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();

      const response = await fetch("https://us-central1-stockmarket-83a73.cloudfunctions.net/cancelListing", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ listingId: selectedAsset.listingId, itemId: selectedAsset.itemId }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Cancellation failed");

      addNotification("Listing removed. Items returned to inventory.", "success");
      if (onPurchaseSuccess) onPurchaseSuccess();
    } catch (err) {
      addNotification(err.message.toUpperCase(), "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmPurchase = async () => {
    setShowConfirm(false);
    setProcessing(true);
    setStatus({ type: "info", message: "EXECUTING..." });

    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();

      const response = await fetch("https://us-central1-stockmarket-83a73.cloudfunctions.net/buyAssetFromExchange", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          itemId: selectedAsset.itemId,
          listingId: selectedAsset.listingId,
          quantity: Number(purchaseQuantity),
        }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Purchase failed");

      addNotification(`Purchased ${purchaseQuantity} unit of ${metadata?.name}.`, "success");
      setStatus({ type: "success", message: "SUCCESSFUL" });
      if (onPurchaseSuccess) setTimeout(() => onPurchaseSuccess(), 1200);
    } catch (err) {
      addNotification(err.message.toUpperCase(), "error");
    } finally {
      setProcessing(false);
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'legendary': return '#ffb100';
      case 'epic': return '#a335ee';
      case 'rare': return '#0070dd';
      case 'uncommon': return '#1eff00';
      default: return '#50E3C2';
    }
  };

  const rawRarity = metadata?.rarity || "common";
  const formattedRarityText = formatRarityName(rawRarity).toUpperCase();
  const rarityColor = getRarityColor(rawRarity);

  const totalCost = (selectedAsset.pricePerUnit || 0) * purchaseQuantity;
  const isUp = (selectedAsset.pricePerUnit || 0) >= (metadata?.lastSoldPrice || 0);

  if (loading) return <div style={{ padding: '20px', color: '#50E3C2', fontFamily: 'JetBrains Mono' }}>LOADING...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontFamily: "'JetBrains Mono', monospace", color: '#eee' }}>
      
      {showConfirm && (
        <ConfirmModal 
          title={confirmType === "buy" ? "CONFIRM PURCHASE" : "CANCEL LISTING"}
          message={confirmType === "buy" 
            ? `Buy ${purchaseQuantity} unit of ${metadata?.name} for $${totalCost.toLocaleString()}?`
            : `Remove this listing? All units will return to your inventory.`
          }
          confirmText={confirmType === "buy" ? "CONFIRM" : "REMOVE"}
          onConfirm={handleConfirmAction}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#fff' }}>{metadata?.name || "NULL_ITEM"}</h2>
          <div style={{ fontSize: '0.6rem', border: `1px solid ${rarityColor}`, color: rarityColor, display: 'inline-block', padding: '2px 6px', marginTop: '4px', letterSpacing: '0.5px' }}>
            {formattedRarityText}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ display: 'block', fontSize: '0.55rem', color: '#8b949e' }}>PRICE</span>
          <span style={{ fontSize: '1rem', color: '#50E3C2', fontWeight: 'bold' }}>${selectedAsset.pricePerUnit?.toLocaleString()}</span>
        </div>
      </div>

      <div style={{ height: '80px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(80, 227, 194, 0.15)', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
        <img 
          src={`/items/${selectedAsset.itemId}.png`} 
          style={{ width: '32px', imageRendering: 'pixelated', filter: `drop-shadow(0 0 10px ${rarityColor}44)` }} 
          onError={(e) => { e.currentTarget.src = '/items/placeholder.png'; }}
          alt={metadata?.name}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={{ background: 'rgba(255,255,255,0.02)', padding: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ display: 'block', fontSize: '0.5rem', color: '#50E3C2' }}>AVAILABLE</span>
          <span style={{ fontSize: '0.7rem' }}>{selectedAsset.quantity} UNITS</span>
        </div>

        <div style={{ background: isOwner ? 'rgba(255,68,68,0.05)' : 'rgba(80,227,194,0.05)', padding: '8px', border: `1px solid ${isOwner ? '#ff444433' : '#50E3C233'}` }}>
          <span style={{ display: 'block', fontSize: '0.5rem', color: isOwner ? '#ff4444' : '#50E3C2' }}>{isOwner ? 'STATUS' : 'QUANTITY '}</span>
          {isOwner ? (
            <span style={{ fontSize: '0.7rem', color: '#ff4444' }}>OWNED</span>
          ) : (
            <input 
              type="number" value={purchaseQuantity} 
              onChange={(e) => setPurchaseQuantity(Math.max(1, Math.min(selectedAsset.quantity, parseInt(e.target.value) || 0)))}
              style={{ width: '100%', background: 'transparent', border: 'none', color: '#fff', fontSize: '0.8rem', outline: 'none' }}
            />
          )}
        </div>

        <div style={{ gridColumn: 'span 2', background: 'rgba(255,255,255,0.02)', padding: '8px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ display: 'block', fontSize: '0.5rem', color: '#8b949e' }}>LAST SOLD</span>
            <span style={{ fontSize: '0.7rem' }}>${metadata?.lastSoldPrice?.toLocaleString() || "---"}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.7rem', color: isUp ? '#ff4444' : '#1eff00' }}>
              {isUp ? '▲' : '▼'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderLeft: `2px solid ${rarityColor}` }}>
        <p style={{ fontSize: '0.7rem', color: '#b0b0b0', margin: 0, lineHeight: '1.4' }}>{metadata?.description || "No data."}</p>
      </div>

      <button 
        onClick={() => handleActionInitiation(isOwner ? "cancel" : "buy")}
        disabled={processing}
        style={{ width: '100%', padding: '12px', background: isOwner ? '#ff4444' : '#50E3C2', border: 'none', color: '#000', fontWeight: 'bold', cursor: processing ? 'not-allowed' : 'pointer' }}
      >
        {processing ? '...' : isOwner ? 'CANCEL' : `BUY ${purchaseQuantity} UNIT(S)`}
      </button>
    </div>
  );
}